import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useWeather } from '../contexts/WeatherContext';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import * as WiIcons from 'react-icons/wi';
import SafeIcon from '../common/SafeIcon';
import SectionHeader from './SectionHeader';

const { FiRefreshCw, FiSearch, FiMapPin } = FiIcons;
const { FaCloudSun, FaCloudRain, FaSnowflake, FaSun, FaCloudBolt, FaCloud } = FaIcons;
const { WiStormWarning } = WiIcons;

const WeatherWidget = () => {
  const { weather, refreshWeather, updateLocation, getCurrentLocation } = useWeather();
  const [currentView, setCurrentView] = useState('current'); // 'current' or 'forecast'
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      updateLocation(searchValue);
      setSearchValue('');
      setShowSearch(false);
    }
  };

  const handleGetLocation = () => {
    getCurrentLocation();
    setShowSearch(false);
  };

  if (weather.loading) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-4">
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aqua-500"></div>
          <span className="ml-2 text-gray-600">Loading weather data...</span>
        </div>
      </div>
    );
  }

  const getWeatherIcon = (condition) => {
    const condition_lower = condition.toLowerCase();
    if (condition_lower.includes('rain') || condition_lower.includes('drizzle')) return FaCloudRain;
    if (condition_lower.includes('snow')) return FaSnowflake;
    if (condition_lower.includes('thunder') || condition_lower.includes('lightning') || condition_lower.includes('storm')) return FaCloudBolt;
    if (condition_lower.includes('cloud')) return FaCloud;
    if (condition_lower.includes('clear') || condition_lower.includes('sun')) return FaSun;
    return FaCloudSun;
  };

  const currentWeather = weather.currentWeather;
  const currentIcon = getWeatherIcon(currentWeather.condition);
  const timestamp = new Date(currentWeather.timestamp);

  // Check for storm warning
  const hasStormWarning = weather.forecast.some(day => {
    const condition = day.condition.toLowerCase();
    return condition.includes('storm') || 
           condition.includes('thunder') || 
           condition.includes('hurricane') || 
           condition.includes('tornado') || 
           condition.includes('warning');
  });

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-4">
      <div className="flex items-center justify-between mb-3">
        <SectionHeader 
          title="Weather" 
          infoText="Local weather conditions help you plan feeding and maintenance activities. Keep an eye on temperature changes and storms that may affect water quality." 
        />
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setCurrentView('current')} 
            className={`px-3 py-1 text-xs rounded-full ${currentView === 'current' ? 'bg-aqua-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Current
          </button>
          <button 
            onClick={() => setCurrentView('forecast')} 
            className={`px-3 py-1 text-xs rounded-full ${currentView === 'forecast' ? 'bg-aqua-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            7-Day Forecast
          </button>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
            title="Search location"
          >
            <SafeIcon icon={FiSearch} className="w-4 h-4" />
          </button>
          <button
            onClick={handleGetLocation}
            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
            title="Use current location"
          >
            <SafeIcon icon={FiMapPin} className="w-4 h-4" />
          </button>
          <button
            onClick={refreshWeather}
            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
            title="Refresh weather data"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="mb-3">
          <form onSubmit={handleSearch} className="flex">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="City name (e.g. London,UK)"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-aqua-500 focus:border-aqua-500"
            />
            <button
              type="submit"
              className="px-3 py-2 bg-aqua-500 text-white rounded-r-lg hover:bg-aqua-600 transition-colors"
            >
              Search
            </button>
          </form>
        </div>
      )}

      {weather.error && (
        <div className="mb-3 p-2 bg-amber-100 border border-amber-300 rounded-lg text-amber-700">
          <p className="text-sm">{weather.error}</p>
        </div>
      )}

      {weather.geoLocationStatus === 'loading' && (
        <div className="mb-3 p-2 bg-blue-100 border border-blue-300 rounded-lg text-blue-700 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
          <span className="text-sm">Detecting your location...</span>
        </div>
      )}

      {hasStormWarning && (
        <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg text-red-700 flex items-center">
          <SafeIcon icon={WiStormWarning} className="w-5 h-5 mr-2" />
          <span className="text-sm font-medium">Storm warning in the forecast! Monitor water quality closely.</span>
        </div>
      )}

      <div className="flex items-center mb-2">
        <SafeIcon icon={FiMapPin} className="w-5 h-5 text-gray-600 mr-1" />
        <span className="text-sm text-gray-600">{weather.location}</span>
      </div>

      {currentView === 'current' ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <SafeIcon icon={currentIcon} className="w-12 h-12 text-aqua-500" />
            <div className="ml-3">
              <p className="text-2xl font-bold">{currentWeather.temperature}°C</p>
              <p className="text-sm text-gray-600">{currentWeather.condition}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Humidity: {currentWeather.humidity}%</p>
            <p className="text-xs text-gray-400">
              Updated: {format(timestamp, 'h:mm a')}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-2">
          <div className="grid grid-cols-7 gap-1">
            {weather.forecast.map((day, index) => {
              const dayIcon = getWeatherIcon(day.condition);
              const date = new Date(day.date);
              return (
                <motion.div 
                  key={index}
                  whileHover={{ y: -2 }}
                  className="text-center"
                >
                  <p className="text-xs text-gray-500">{format(date, 'EEE')}</p>
                  <div className="my-1">
                    <SafeIcon icon={dayIcon} className="w-6 h-6 text-aqua-500 mx-auto" />
                  </div>
                  <p className="text-sm font-medium">{day.temperature}°C</p>
                  <p className="text-xs text-gray-500 truncate">{day.condition}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherWidget;